import React, { useEffect, useState } from 'react';
import { Loader2, AlertCircle, X, CheckSquare } from 'lucide-react';
import { closuresService } from '@/services/closures';
import { ratingsService } from '@/services/ratings';
import { toast } from 'sonner';

interface AppointmentClosureModalProps {
  isOpen: boolean;
  onClose: () => void;
  appointmentId: string;
  evolutionContent: string;
  onSuccess?: () => void;
}

export const AppointmentClosureModal: React.FC<AppointmentClosureModalProps> = ({
  isOpen,
  onClose,
  appointmentId,
  evolutionContent,
  onSuccess,
}) => {
  const [clinicalSummary, setClinicalSummary] = useState<string>('');
  const [finalNotes, setFinalNotes] = useState<string>('');
  const [patientGuidance, setPatientGuidance] = useState<string>('');
  const [internalObservation, setInternalObservation] = useState<string>('');

  // Retorno
  const [followUpOption, setFollowUpOption] = useState<'REQUIRED' | 'NONE' | 'OPTIONAL'>('NONE');
  const [followUpPreset, setFollowUpPreset] = useState<'7_days' | '15_days' | '1_month' | 'exact'>('15_days');
  const [suggestedDate, setSuggestedDate] = useState<string>('');

  // Avaliação do Paciente
  const [patientRating, setPatientRating] = useState<number>(0);
  const [patientComment, setPatientComment] = useState<string>('');
  const [wouldRecommend, setWouldRecommend] = useState<boolean>(true);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const formatDateInputValue = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const addCalendarMonths = (baseDate: Date, months: number) => {
    const target = new Date(baseDate);
    const originalDay = target.getDate();
    target.setDate(1);
    target.setMonth(target.getMonth() + months);
    const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
    target.setDate(Math.min(originalDay, lastDay));
    return target;
  };

  const calculatePresetDate = (preset: typeof followUpPreset) => {
    const date = new Date();
    if (preset === '7_days') date.setDate(date.getDate() + 7);
    if (preset === '15_days') date.setDate(date.getDate() + 15);
    if (preset === '1_month') return addCalendarMonths(date, 1);
    return date;
  };

  const getFollowUpType = (): 'days' | 'weeks' | 'months' | 'exact' => {
    if (followUpPreset === '7_days') return 'weeks';
    if (followUpPreset === '1_month') return 'months';
    if (followUpPreset === 'exact') return 'exact';
    return 'days';
  };

  useEffect(() => {
    if (followUpOption !== 'REQUIRED') {
      setSuggestedDate('');
      return;
    }

    if (followUpPreset !== 'exact') {
      setSuggestedDate(formatDateInputValue(calculatePresetDate(followUpPreset)));
    }
  }, [followUpOption, followUpPreset]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!clinicalSummary.trim()) {
      setError('Por favor, preencha o Resumo Clínico.');
      return;
    }
    if (patientRating === 0) {
      setError('Por favor, avalie o paciente.');
      return;
    }
    if (followUpOption === 'REQUIRED' && !suggestedDate) {
      setError('Por favor, informe a data do retorno.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const payload = {
        appointmentId,
        clinicalSummary: clinicalSummary.trim(),
        finalNotes: finalNotes.trim() || undefined,
        patientGuidance: patientGuidance.trim() || undefined,
        followUpRequired: followUpOption === 'REQUIRED',
        followUpType: followUpOption === 'REQUIRED' ? getFollowUpType() : null,
        suggestedFollowUpDate: followUpOption === 'REQUIRED' && suggestedDate ? suggestedDate : null,
        noFollowUpNeeded: followUpOption === 'NONE',
        internalObservation: internalObservation.trim() || undefined,
        evolutionContent,
      };

      const response = await closuresService.createClosure(payload);

      if (response.success) {
        // Enviar avaliação
        try {
          await ratingsService.submitRating({
            appointmentId,
            rating: patientRating,
            comment: patientComment || undefined,
            wouldRecommend,
          });
        } catch (ratingErr) {
          console.error('Erro ao enviar avaliação:', ratingErr);
          toast.warning('Fechamento salvo, mas houve erro ao avaliar o paciente.');
        }

        toast.success('Consulta finalizada e prontuário selado com sucesso!');
        if (onSuccess) onSuccess();
        onClose();
      } else {
        setError(response.error || 'Erro ao registrar fechamento clínico. Tente novamente.');
      }
    } catch (err) {
      setError('Falha de conexão com o servidor.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      <div className="relative bg-white dark:bg-neutral-900 rounded-3xl w-full max-w-xl overflow-hidden shadow-2xl border border-neutral-200 dark:border-neutral-800 flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-200 dark:border-neutral-800 shrink-0">
          <div className="flex items-center gap-2">
            <CheckSquare className="w-5 h-5 text-teal-600" />
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Fechamento Clínico da Consulta</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-white hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">
          {error && (
            <div className="p-4 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900/50 rounded-2xl flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
            </div>
          )}

          {/* Resumo Clínico */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="clinicalSummary" className="text-sm font-bold text-gray-700 dark:text-gray-300 flex items-center gap-1">
              Resumo Clínico da Consulta <span className="text-red-500">*</span>
            </label>
            <textarea
              id="clinicalSummary"
              value={clinicalSummary}
              onChange={(e) => setClinicalSummary(e.target.value)}
              placeholder="Resuma o diagnóstico, evolução e pontos principais do atendimento..."
              rows={3}
              required
              className="w-full px-4 py-3 rounded-2xl border border-neutral-200 dark:border-neutral-800 dark:bg-neutral-950 text-gray-800 dark:text-gray-200 text-sm focus:ring-2 focus:ring-teal-500 focus:outline-none resize-none"
            />
          </div>

          {/* Orientação ao Paciente */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="patientGuidance" className="text-sm font-bold text-gray-700 dark:text-gray-300">
              Orientação ao Paciente (Visível para o paciente)
            </label>
            <textarea
              id="patientGuidance"
              value={patientGuidance}
              onChange={(e) => setPatientGuidance(e.target.value)}
              placeholder="Instruções pós-consulta, medicamentos, cuidados domiciliares..."
              rows={2}
              className="w-full px-4 py-3 rounded-2xl border border-neutral-200 dark:border-neutral-800 dark:bg-neutral-950 text-gray-800 dark:text-gray-200 text-sm focus:ring-2 focus:ring-teal-500 focus:outline-none resize-none"
            />
          </div>

          {/* Observações Finais */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="finalNotes" className="text-sm font-bold text-gray-700 dark:text-gray-300">
              Observações Clínicas Gerais
            </label>
            <textarea
              id="finalNotes"
              value={finalNotes}
              onChange={(e) => setFinalNotes(e.target.value)}
              placeholder="Outras anotações gerais relevantes sobre o atendimento..."
              rows={2}
              className="w-full px-4 py-3 rounded-2xl border border-neutral-200 dark:border-neutral-800 dark:bg-neutral-950 text-gray-800 dark:text-gray-200 text-sm focus:ring-2 focus:ring-teal-500 focus:outline-none resize-none"
            />
          </div>

          {/* Necessidade de Retorno */}
          <div className="p-4 bg-neutral-50 dark:bg-neutral-800/40 rounded-2xl border border-neutral-100 dark:border-neutral-800/60 space-y-4">
            <span className="text-sm font-bold text-gray-800 dark:text-gray-200 block">Continuidade e Retorno</span>
            
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => setFollowUpOption('NONE')}
                className={`py-2 px-4 rounded-xl text-xs font-bold transition-all border ${
                  followUpOption === 'NONE'
                    ? 'bg-neutral-800 text-white border-neutral-800 dark:bg-neutral-200 dark:text-black dark:border-neutral-200'
                    : 'bg-white dark:bg-neutral-800 border-neutral-200 dark:border-neutral-700 text-gray-700 dark:text-gray-300 hover:bg-neutral-50'
                }`}
              >
                Não necessita retorno
              </button>
              <button
                type="button"
                onClick={() => setFollowUpOption('OPTIONAL')}
                className={`py-2 px-4 rounded-xl text-xs font-bold transition-all border ${
                  followUpOption === 'OPTIONAL'
                    ? 'bg-neutral-800 text-white border-neutral-800 dark:bg-neutral-200 dark:text-black dark:border-neutral-200'
                    : 'bg-white dark:bg-neutral-800 border-neutral-200 dark:border-neutral-700 text-gray-700 dark:text-gray-300 hover:bg-neutral-50'
                }`}
              >
                Retorno opcional
              </button>
              <button
                type="button"
                onClick={() => setFollowUpOption('REQUIRED')}
                className={`py-2 px-4 rounded-xl text-xs font-bold transition-all border ${
                  followUpOption === 'REQUIRED'
                    ? 'bg-teal-600 text-white border-teal-600'
                    : 'bg-white dark:bg-neutral-800 border-neutral-200 dark:border-neutral-700 text-gray-700 dark:text-gray-300 hover:bg-neutral-50'
                }`}
              >
                Necessita retorno
              </button>
            </div>

            {followUpOption === 'REQUIRED' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 animate-fade-in">
                <div className="flex flex-col gap-1">
                  <label htmlFor="followUpType" className="text-xs font-semibold text-gray-600 dark:text-gray-400">
                    Quando deve retornar
                  </label>
                  <select
                    id="followUpType"
                    value={followUpPreset}
                    onChange={(e) => setFollowUpPreset(e.target.value as typeof followUpPreset)}
                    className="px-3 py-2 rounded-xl border border-neutral-200 dark:border-neutral-700 dark:bg-neutral-900 text-sm focus:outline-none"
                  >
                    <option value="7_days">Daqui 1 semana</option>
                    <option value="15_days">Daqui 15 dias</option>
                    <option value="1_month">Daqui 1 mês</option>
                    <option value="exact">Data específica</option>
                  </select>
                </div>

                <div className="flex flex-col gap-1">
                  <label htmlFor="suggestedDate" className="text-xs font-semibold text-gray-600 dark:text-gray-400">
                    Data sugerida
                  </label>
                  <input
                    type="date"
                    id="suggestedDate"
                    value={suggestedDate}
                    onChange={(e) => setSuggestedDate(e.target.value)}
                    disabled={followUpPreset !== 'exact'}
                    className="px-3 py-2 rounded-xl border border-neutral-200 dark:border-neutral-700 dark:bg-neutral-900 text-sm focus:outline-none"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Observação Interna Privada */}
          <div className="flex flex-col gap-1.5 p-4 bg-amber-50/30 dark:bg-amber-950/5 border border-amber-200/50 dark:border-amber-900/10 rounded-2xl">
            <label htmlFor="internalObservation" className="text-sm font-bold text-amber-800 dark:text-amber-400 flex items-center gap-1.5">
              <span>Anotações Internas Clínicas</span>
              <span className="text-[10px] bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-400 px-2 py-0.5 rounded font-mono font-bold">Privado/Confidencial</span>
            </label>
            <textarea
              id="internalObservation"
              value={internalObservation}
              onChange={(e) => setInternalObservation(e.target.value)}
              placeholder="Informações úteis para outros médicos autorizados em atendimentos futuros (Não compartilhado com o paciente)..."
              rows={2}
              className="w-full px-4 py-3 rounded-2xl border border-neutral-200 dark:border-neutral-800 dark:bg-neutral-950 text-gray-800 dark:text-gray-200 text-sm focus:ring-2 focus:ring-amber-500 focus:outline-none resize-none"
            />
          </div>

          {/* Avaliação do Paciente */}
          <div className="flex flex-col gap-1.5 p-4 bg-teal-50/50 dark:bg-teal-950/10 border border-teal-200/50 dark:border-teal-900/30 rounded-2xl">
            <label className="text-sm font-bold text-teal-800 dark:text-teal-400 flex items-center gap-1.5">
              <span>Avaliação do Paciente</span>
              <span className="text-red-500">*</span>
            </label>
            <p className="text-xs text-teal-700/70 dark:text-teal-400/70 mb-2">Ajude outros médicos a conhecerem este paciente.</p>
            
            <div className="flex items-center gap-2 mb-3">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setPatientRating(star)}
                  className={`p-1 transition-transform hover:scale-110 ${
                    patientRating >= star ? 'text-amber-400' : 'text-gray-300 dark:text-gray-600'
                  }`}
                >
                  <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                </button>
              ))}
            </div>

            <textarea
              value={patientComment}
              onChange={(e) => setPatientComment(e.target.value)}
              placeholder="O paciente foi colaborativo? (Opcional)"
              rows={2}
              className="w-full px-4 py-3 rounded-2xl border border-neutral-200 dark:border-neutral-800 dark:bg-neutral-950 text-gray-800 dark:text-gray-200 text-sm focus:ring-2 focus:ring-teal-500 focus:outline-none resize-none mb-3"
            />

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={wouldRecommend}
                onChange={(e) => setWouldRecommend(e.target.checked)}
                className="w-4 h-4 text-teal-600 rounded border-gray-300 focus:ring-teal-500 dark:bg-neutral-800 dark:border-neutral-700"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                Recomendo este paciente para outros médicos
              </span>
            </label>
          </div>
        </form>

        {/* Footer */}
        <div className="p-6 border-t border-neutral-200 dark:border-neutral-800 bg-neutral-50/50 dark:bg-neutral-900/50 flex gap-3 shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 py-3 px-4 border border-neutral-200 dark:border-neutral-800 text-sm font-bold text-gray-700 dark:text-gray-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-2xl transition-colors disabled:opacity-50"
          >
            Voltar ao Prontuário
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isLoading || !clinicalSummary.trim()}
            className="flex-1 py-3 px-4 bg-teal-600 hover:bg-teal-700 text-white text-sm font-bold rounded-2xl transition-all shadow-md shadow-teal-600/10 hover:shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Finalizando...
              </>
            ) : (
              <>
                Confirmar Finalização
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
